import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import { CategoryGroupConnection } from './CategoryGroupConnection';
import type { CupType } from './../enums/CupType';
import type { EntityState } from './../enums/EntityState';
import type { Gender } from './../enums/Gender';
import type { PrizeShareType } from './../enums/PrizeShareType';
import { formatDateIso } from '@/utilities/dateutils';

export class Category extends BaseObj {
    public ID: number;
    public __id: number;

    public AgeFrom: number | null = 1;
    public __agefrom: number | null = 1;

    public AgeTo: number | null = 150;
    public __ageto: number | null = 150;

    public BibFrom: number | null;
    public __bibfrom: number | null;

    public BibTo: number | null;
    public __bibto: number | null;

    public CategoryGroupConnection: CategoryGroupConnection[];
    public __categorygroupconnection: CategoryGroupConnection[];

    public CreateRankingHeats: boolean;
    public __createrankingheats: boolean;

    public CupType: CupType | null;
    public __cuptype: CupType | null;

    public DisplayTimes: boolean = true;
    public __displaytimes: boolean = true;

    public EventID: number;
    public __eventid: number;

    public Gender: Gender | null;
    public __gender: Gender | null;

    public HeatStartTime: Date | null;
    public __heatstarttime: Date | null;

    public LapCount: number;
    public __lapcount: number;

    public MaxIntoHeats: number | null;
    public __maxintoheats: number | null;

    public Name: string;
    public __name: string;

    public PrizeShare: number | null;
    public __prizeshare: number | null;

    public PrizeShareType: PrizeShareType | null;
    public __prizesharetype: PrizeShareType | null;

    public Ranking: boolean = true;
    public __ranking: boolean = true;

    public ReserveBibs: number;
    public __reservebibs: number;

    public ReserveBibsInFront: boolean;
    public __reservebibsinfront: boolean;

    public ShootingPenalty: ITimeSpan | null;
    public __shootingpenalty: ITimeSpan | null;

    public SimultaneousStarts: number | null;
    public __simultaneousstarts: number | null;

    public Sort: number | null;
    public __sort: number | null;

    public StartDelay: ITimeSpan | null;
    public __startdelay: ITimeSpan | null;

    public StartInterval: ITimeSpan | null;
    public __startinterval: ITimeSpan | null;

    public StartTime: Date | null;
    public __starttime: Date | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AgeFrom?: number | null;
        AgeTo?: number | null;
        BibFrom?: number | null;
        BibTo?: number | null;
        CategoryGroupConnection?: CategoryGroupConnection[];
        CreateRankingHeats?: boolean;
        CupType?: CupType | null;
        DisplayTimes?: boolean;
        EntityState?: EntityState;
        EventID?: number;
        Gender?: Gender | null;
        HeatStartTime?: Date | null;
        LapCount?: number;
        MaxIntoHeats?: number | null;
        Name?: string | null;
        PrizeShare?: number | null;
        PrizeShareType?: PrizeShareType | null;
        Ranking?: boolean;
        ReserveBibs?: number;
        ReserveBibsInFront?: boolean;
        ShootingPenalty?: ITimeSpan | null;
        SimultaneousStarts?: number | null;
        Sort?: number | null;
        StartDelay?: ITimeSpan | null;
        StartInterval?: ITimeSpan | null;
        StartTime?: Date | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Category>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Category tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AgeFrom = values.AgeFrom ?? 1;
        this.__agefrom = values.AgeFrom ?? 1;
        Object.defineProperty(this, 'AgeFrom', {
            get(): number | null {
                return this.__agefrom;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('AgeFrom', this.__agefrom, value);
                this.__agefrom = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AgeTo = values.AgeTo ?? 150;
        this.__ageto = values.AgeTo ?? 150;
        Object.defineProperty(this, 'AgeTo', {
            get(): number | null {
                return this.__ageto;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('AgeTo', this.__ageto, value);
                this.__ageto = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.BibFrom = values.BibFrom ?? null;
        this.__bibfrom = values.BibFrom ?? null;
        Object.defineProperty(this, 'BibFrom', {
            get(): number | null {
                return this.__bibfrom;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('BibFrom', this.__bibfrom, value);
                this.__bibfrom = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.BibTo = values.BibTo ?? null;
        this.__bibto = values.BibTo ?? null;
        Object.defineProperty(this, 'BibTo', {
            get(): number | null {
                return this.__bibto;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('BibTo', this.__bibto, value);
                this.__bibto = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryGroupConnection = values.CategoryGroupConnection ?? [];
        this.__categorygroupconnection = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.CategoryGroupConnection ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', newVal),
        });
        this.__categorygroupconnection.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', this.CategoryGroupConnection),
            );
        });
        Object.defineProperty(this, 'CategoryGroupConnection', {
            get(): CategoryGroupConnection[] {
                return this.__categorygroupconnection;
            },
            set(value: CategoryGroupConnection[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__categorygroupconnection = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: CategoryGroupConnection[], newVal: CategoryGroupConnection[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', newVal),
                });
                this.__categorygroupconnection.forEach((child: CategoryGroupConnection) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', this.CategoryGroupConnection),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('CategoryGroupConnection', this.CategoryGroupConnection);
            },
            enumerable: true,
            configurable: true,
        });

        this.CreateRankingHeats = values.CreateRankingHeats ?? false;
        this.__createrankingheats = values.CreateRankingHeats ?? false;
        Object.defineProperty(this, 'CreateRankingHeats', {
            get(): boolean {
                return this.__createrankingheats;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('CreateRankingHeats', this.__createrankingheats, value);
                this.__createrankingheats = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CupType = values.CupType ?? 'None';
        this.__cuptype = values.CupType ?? 'None';
        Object.defineProperty(this, 'CupType', {
            get(): CupType | null {
                return this.__cuptype;
            },
            set(value: CupType | null) {
                this.__entityStateHandler.propertyChanged('CupType', this.__cuptype, value);
                this.__cuptype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DisplayTimes = values.DisplayTimes ?? true;
        this.__displaytimes = values.DisplayTimes ?? true;
        Object.defineProperty(this, 'DisplayTimes', {
            get(): boolean {
                return this.__displaytimes;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('DisplayTimes', this.__displaytimes, value);
                this.__displaytimes = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Gender = values.Gender ?? 'm';
        this.__gender = values.Gender ?? 'm';
        Object.defineProperty(this, 'Gender', {
            get(): Gender | null {
                return this.__gender;
            },
            set(value: Gender | null) {
                this.__entityStateHandler.propertyChanged('Gender', this.__gender, value);
                this.__gender = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HeatStartTime = values.HeatStartTime ?? null;
        this.__heatstarttime = values.HeatStartTime ?? null;
        Object.defineProperty(this, 'HeatStartTime', {
            get(): Date | null {
                return this.__heatstarttime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('HeatStartTime', this.__heatstarttime, value);
                this.__heatstarttime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LapCount = values.LapCount ?? 0;
        this.__lapcount = values.LapCount ?? 0;
        Object.defineProperty(this, 'LapCount', {
            get(): number {
                return this.__lapcount;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('LapCount', this.__lapcount, value);
                this.__lapcount = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MaxIntoHeats = values.MaxIntoHeats ?? null;
        this.__maxintoheats = values.MaxIntoHeats ?? null;
        Object.defineProperty(this, 'MaxIntoHeats', {
            get(): number | null {
                return this.__maxintoheats;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('MaxIntoHeats', this.__maxintoheats, value);
                this.__maxintoheats = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PrizeShare = values.PrizeShare ?? null;
        this.__prizeshare = values.PrizeShare ?? null;
        Object.defineProperty(this, 'PrizeShare', {
            get(): number | null {
                return this.__prizeshare;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('PrizeShare', this.__prizeshare, value);
                this.__prizeshare = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PrizeShareType = values.PrizeShareType ?? 'None';
        this.__prizesharetype = values.PrizeShareType ?? 'None';
        Object.defineProperty(this, 'PrizeShareType', {
            get(): PrizeShareType | null {
                return this.__prizesharetype;
            },
            set(value: PrizeShareType | null) {
                this.__entityStateHandler.propertyChanged('PrizeShareType', this.__prizesharetype, value);
                this.__prizesharetype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Ranking = values.Ranking ?? true;
        this.__ranking = values.Ranking ?? true;
        Object.defineProperty(this, 'Ranking', {
            get(): boolean {
                return this.__ranking;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Ranking', this.__ranking, value);
                this.__ranking = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReserveBibs = values.ReserveBibs ?? 0;
        this.__reservebibs = values.ReserveBibs ?? 0;
        Object.defineProperty(this, 'ReserveBibs', {
            get(): number {
                return this.__reservebibs;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ReserveBibs', this.__reservebibs, value);
                this.__reservebibs = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ReserveBibsInFront = values.ReserveBibsInFront ?? false;
        this.__reservebibsinfront = values.ReserveBibsInFront ?? false;
        Object.defineProperty(this, 'ReserveBibsInFront', {
            get(): boolean {
                return this.__reservebibsinfront;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('ReserveBibsInFront', this.__reservebibsinfront, value);
                this.__reservebibsinfront = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShootingPenalty = values.ShootingPenalty ?? null;
        this.__shootingpenalty = values.ShootingPenalty ?? null;
        Object.defineProperty(this, 'ShootingPenalty', {
            get(): ITimeSpan | null {
                return this.__shootingpenalty;
            },
            set(value: ITimeSpan | null) {
                this.__entityStateHandler.propertyChanged('ShootingPenalty', this.__shootingpenalty, value);
                this.__shootingpenalty = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SimultaneousStarts = values.SimultaneousStarts ?? null;
        this.__simultaneousstarts = values.SimultaneousStarts ?? null;
        Object.defineProperty(this, 'SimultaneousStarts', {
            get(): number | null {
                return this.__simultaneousstarts;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('SimultaneousStarts', this.__simultaneousstarts, value);
                this.__simultaneousstarts = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sort = values.Sort ?? null;
        this.__sort = values.Sort ?? null;
        Object.defineProperty(this, 'Sort', {
            get(): number | null {
                return this.__sort;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Sort', this.__sort, value);
                this.__sort = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartDelay = values.StartDelay ?? null;
        this.__startdelay = values.StartDelay ?? null;
        Object.defineProperty(this, 'StartDelay', {
            get(): ITimeSpan | null {
                return this.__startdelay;
            },
            set(value: ITimeSpan | null) {
                this.__entityStateHandler.propertyChanged('StartDelay', this.__startdelay, value);
                this.__startdelay = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartInterval = values.StartInterval ?? null;
        this.__startinterval = values.StartInterval ?? null;
        Object.defineProperty(this, 'StartInterval', {
            get(): ITimeSpan | null {
                return this.__startinterval;
            },
            set(value: ITimeSpan | null) {
                this.__entityStateHandler.propertyChanged('StartInterval', this.__startinterval, value);
                this.__startinterval = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartTime = values.StartTime ?? null;
        this.__starttime = values.StartTime ?? null;
        Object.defineProperty(this, 'StartTime', {
            get(): Date | null {
                return this.__starttime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('StartTime', this.__starttime, value);
                this.__starttime = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AgeFrom": ${JSON.stringify(this.AgeFrom)}` +
            ',' +
            `"AgeTo": ${JSON.stringify(this.AgeTo)}` +
            ',' +
            `"BibFrom": ${JSON.stringify(this.BibFrom)}` +
            ',' +
            `"BibTo": ${JSON.stringify(this.BibTo)}` +
            ',' +
            `"CategoryGroupConnection": [ ${this.CategoryGroupConnection.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"CreateRankingHeats": ${JSON.stringify(this.CreateRankingHeats)}` +
            ',' +
            `"CupType": ${this.CupType == null ? null : this.CupType == 'None' ? 0 : this.CupType == 'NSF_PROLOG_TO_FINALS' ? 1 : this.CupType == 'NSF_30_WITH_TIMING_LL_FROM_LEVELS' ? 2 : this.CupType == 'NSF_30_WITH_TIMING_LL_FROM_PROLOG' ? 3 : this.CupType == 'NSF_6' ? 4 : this.CupType == 'NSF_12' ? 5 : this.CupType == 'NSF_16' ? 6 : this.CupType == 'NSF_18' ? 7 : this.CupType == 'NSF_24' ? 8 : this.CupType == 'NSF_50' ? 9 : this.CupType == 'NSF_UP_AND_DOWN_1X4' ? 10 : this.CupType == 'NSF_UP_AND_DOWN_1X6' ? 11 : this.CupType == 'NSF_UP_AND_DOWN_2X4' ? 12 : this.CupType == 'NSF_UP_AND_DOWN_2X6' ? 13 : this.CupType == 'NSF_UP_AND_DOWN_3X6' ? 14 : this.CupType == 'NSF_UP_AND_DOWN_3X4' ? 15 : null}` +
            ',' +
            `"DisplayTimes": ${JSON.stringify(this.DisplayTimes)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Gender": ${this.Gender == null ? null : this.Gender == 'm' ? 0 : this.Gender == 'f' ? 1 : this.Gender == 'n' ? 2 : null}` +
            ',' +
            `"HeatStartTime": ${this.HeatStartTime ? '"' + formatDateIso(this.HeatStartTime) + '"' : null}` +
            ',' +
            `"LapCount": ${JSON.stringify(this.LapCount)}` +
            ',' +
            `"MaxIntoHeats": ${JSON.stringify(this.MaxIntoHeats)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"PrizeShare": ${JSON.stringify(this.PrizeShare)}` +
            ',' +
            `"PrizeShareType": ${this.PrizeShareType == null ? null : this.PrizeShareType == 'None' ? 0 : this.PrizeShareType == 'PercentageOfStarting' ? 1 : this.PrizeShareType == 'PercentageOfAll' ? 2 : this.PrizeShareType == 'Fixed' ? 3 : null}` +
            ',' +
            `"Ranking": ${JSON.stringify(this.Ranking)}` +
            ',' +
            `"ReserveBibs": ${JSON.stringify(this.ReserveBibs)}` +
            ',' +
            `"ReserveBibsInFront": ${JSON.stringify(this.ReserveBibsInFront)}` +
            ',' +
            `"ShootingPenalty": ${this.ShootingPenalty ? '"' + this.ShootingPenalty?.serialize() + '"' : null}` +
            ',' +
            `"SimultaneousStarts": ${JSON.stringify(this.SimultaneousStarts)}` +
            ',' +
            `"Sort": ${JSON.stringify(this.Sort)}` +
            ',' +
            `"StartDelay": ${this.StartDelay ? '"' + this.StartDelay?.serialize() + '"' : null}` +
            ',' +
            `"StartInterval": ${this.StartInterval ? '"' + this.StartInterval?.serialize() + '"' : null}` +
            ',' +
            `"StartTime": ${this.StartTime ? '"' + formatDateIso(this.StartTime) + '"' : null}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Category from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AgeFrom?: number | null;
        AgeTo?: number | null;
        BibFrom?: number | null;
        BibTo?: number | null;
        CategoryGroupConnection?: CategoryGroupConnection[];
        CreateRankingHeats?: boolean;
        CupType?: CupType | null;
        DisplayTimes?: boolean;
        EventID?: number;
        Gender?: Gender | null;
        HeatStartTime?: Date | null;
        LapCount?: number;
        MaxIntoHeats?: number | null;
        Name?: string | null;
        PrizeShare?: number | null;
        PrizeShareType?: PrizeShareType | null;
        Ranking?: boolean;
        ReserveBibs?: number;
        ReserveBibsInFront?: boolean;
        ShootingPenalty?: ITimeSpan | null;
        SimultaneousStarts?: number | null;
        Sort?: number | null;
        StartDelay?: ITimeSpan | null;
        StartInterval?: ITimeSpan | null;
        StartTime?: Date | null;
        EntityState?: 'Added' | 'Unchanged';
    }): Category {
        return new Category(values);
    }

    public static _FromServerJson(json: any): Category {
        return new Category({
            ID: json.ID,
            AgeFrom: json.AgeFrom ?? null,
            AgeTo: json.AgeTo ?? null,
            BibFrom: json.BibFrom ?? null,
            BibTo: json.BibTo ?? null,
            CategoryGroupConnection: json.CategoryGroupConnection?.map((x: any) => CategoryGroupConnection._FromServerJson(x)),
            CreateRankingHeats: json.CreateRankingHeats,
            CupType:
                json.CupType == null
                    ? null
                    : json.CupType == 0
                      ? 'None'
                      : json.CupType == 1
                        ? 'NSF_PROLOG_TO_FINALS'
                        : json.CupType == 2
                          ? 'NSF_30_WITH_TIMING_LL_FROM_LEVELS'
                          : json.CupType == 3
                            ? 'NSF_30_WITH_TIMING_LL_FROM_PROLOG'
                            : json.CupType == 4
                              ? 'NSF_6'
                              : json.CupType == 5
                                ? 'NSF_12'
                                : json.CupType == 6
                                  ? 'NSF_16'
                                  : json.CupType == 7
                                    ? 'NSF_18'
                                    : json.CupType == 8
                                      ? 'NSF_24'
                                      : json.CupType == 9
                                        ? 'NSF_50'
                                        : json.CupType == 10
                                          ? 'NSF_UP_AND_DOWN_1X4'
                                          : json.CupType == 11
                                            ? 'NSF_UP_AND_DOWN_1X6'
                                            : json.CupType == 12
                                              ? 'NSF_UP_AND_DOWN_2X4'
                                              : json.CupType == 13
                                                ? 'NSF_UP_AND_DOWN_2X6'
                                                : json.CupType == 14
                                                  ? 'NSF_UP_AND_DOWN_3X6'
                                                  : json.CupType == 15
                                                    ? 'NSF_UP_AND_DOWN_3X4'
                                                    : null,
            DisplayTimes: json.DisplayTimes,
            EventID: json.EventID,
            Gender: json.Gender == null ? null : json.Gender == 0 ? 'm' : json.Gender == 1 ? 'f' : json.Gender == 2 ? 'n' : null,
            HeatStartTime: json.HeatStartTime
                ? json.HeatStartTime instanceof Date
                    ? json.HeatStartTime
                    : new Date(json.HeatStartTime)
                : null,
            LapCount: json.LapCount,
            MaxIntoHeats: json.MaxIntoHeats ?? null,
            Name: json.Name ?? '',
            PrizeShare: json.PrizeShare ?? null,
            PrizeShareType:
                json.PrizeShareType == null
                    ? null
                    : json.PrizeShareType == 0
                      ? 'None'
                      : json.PrizeShareType == 1
                        ? 'PercentageOfStarting'
                        : json.PrizeShareType == 2
                          ? 'PercentageOfAll'
                          : json.PrizeShareType == 3
                            ? 'Fixed'
                            : null,
            Ranking: json.Ranking,
            ReserveBibs: json.ReserveBibs,
            ReserveBibsInFront: json.ReserveBibsInFront,
            ShootingPenalty: json.ShootingPenalty ? TimeSpan.FromTimeString(json.ShootingPenalty) : null,
            SimultaneousStarts: json.SimultaneousStarts ?? null,
            Sort: json.Sort ?? null,
            StartDelay: json.StartDelay ? TimeSpan.FromTimeString(json.StartDelay) : null,
            StartInterval: json.StartInterval ? TimeSpan.FromTimeString(json.StartInterval) : null,
            StartTime: json.StartTime ? (json.StartTime instanceof Date ? json.StartTime : new Date(json.StartTime)) : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Category from another instance of Category.
     * @param other The Category you want to clone
     * @returns A cloned Category
     */
    public static FromOther(other: Category): Category {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Category._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Category.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'AgeFrom'
            | 'AgeTo'
            | 'BibFrom'
            | 'BibTo'
            | 'CategoryGroupConnection'
            | 'CreateRankingHeats'
            | 'CupType'
            | 'DisplayTimes'
            | 'EventID'
            | 'Gender'
            | 'HeatStartTime'
            | 'LapCount'
            | 'MaxIntoHeats'
            | 'Name'
            | 'PrizeShare'
            | 'PrizeShareType'
            | 'Ranking'
            | 'ReserveBibs'
            | 'ReserveBibsInFront'
            | 'ShootingPenalty'
            | 'SimultaneousStarts'
            | 'Sort'
            | 'StartDelay'
            | 'StartInterval'
            | 'StartTime'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Category';
}
